import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import styled from 'styled-components';
import CloudflareImage from '../components/ui/cloudflare-image';
import { verifyEmail, resendVerification } from '../services/api';

// Add a spacer component to push content below navbar
const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;



export default function VerifyEmailPage() {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  useEffect(() => {
    // Get email from query params
    const params = new URLSearchParams(location.search);
    const emailParam = params.get('email');

    if (emailParam) {
      setEmail(emailParam);
    } else {
      setError('Email address is missing. Please go back to the registration page.');
    }

    // Check if user is already authenticated
    if (isAuthenticated()) {
      const fromPath = new URLSearchParams(location.search).get('from') || '/';
      navigate(fromPath);
    }
  }, [location, isAuthenticated, navigate]);

  const handleVerifyEmail = async (e) => {
    e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await verifyEmail({
        email,
        verificationCode
      });

      if (response.success) {
        setSuccess(true);

        // Auto-login the user
        await login(response.user, true);

        // Redirect to the original page or profile
        const params = new URLSearchParams(location.search);
        const fromPath = params.get('from') || '/profile';

        setTimeout(() => {
          navigate(fromPath);
        }, 2000);
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
        'Failed to verify email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      setError('Email address is missing. Please go back to the registration page.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await resendVerification(email);

      if (response.success) {
        setError('A new verification code has been sent to your email');
      }
    } catch (error) {
      setError(
        error.error ||
        'Failed to resend verification code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 px-4 py-12">
      <NavbarSpacer />
      <Card className="mx-auto w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center">
            <CloudflareImage
              src="/images/TMZ L.png"
              alt="TheMadZone"
              className="h-12 mb-8"
            />
          </div>
          <CardTitle className="text-2xl font-bold">Verify Your Email</CardTitle>
          <CardDescription>
            {success
              ? 'Your email has been verified successfully! Redirecting to your profile...'
              : `Please enter the 6-digit code sent to ${email || 'your email'}`
            }
          </CardDescription>
        </CardHeader>
        {!success && (
          <CardContent>
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="verificationCode" className="text-sm font-medium leading-none">
                  Verification Code
                </label>
                <input
                  id="verificationCode"
                  type="text"
                  className={cn(
                    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center tracking-widest",
                    error && "border-destructive"
                  )}
                  placeholder="123456"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  maxLength={6}
                />
                {error && (
                  <div className="text-sm font-medium text-destructive">
                    {error}
                  </div>
                )}
              </div>
              <div className="flex flex-col space-y-2">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleResendCode}
                  disabled={loading}
                >
                  Resend Code
                </Button>
              </div>
            </form>
          </CardContent>
        )}
        <CardFooter className="flex flex-col space-y-4">
          <div className="text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">
              Back to Login
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
